import {auto} from "angular";
import {MediaTypes} from "../services/local-data/data/MediaTypes";

interface LocalDataFileSelectorBindings {
    onSelectFile(data: { file: File, fileType: any, dataProjection: string }): void;
}

function asArray<T>(tOrTs: T|T[]): T[] {
    return Array.isArray(tOrTs) ? tOrTs : [tOrTs];
}

export function fileMatches(file: File, extension: string|string[], mediaType: string|string[]): boolean {
    mediaType = asArray(mediaType);
    extension = asArray(extension);

    if (mediaType.includes(file.type)) return true;

    const ext = file.name.split('.').slice(-1)[0];
    return extension.includes(ext);
}

class LocalDataFileSelectorController implements ng.IComponentController, LocalDataFileSelectorBindings {
    constructor(protected $element: JQLite, protected $scope: ng.IScope) {

    }

    public file: File;
    public extraFiles: File[] = [];

    public fileTypes = [
        {
            id: 'auto',
            label: 'Auto detect file type'
        },
        {
            id: 'kml',
            label: 'KML - Keyhole Markup Language',
            extension: 'kml',
            mediaType: 'application/vnd.google-earth.kml+xml'
        },
        {
            id: 'kmz',
            label: 'KMZ - Keyhole Markup Language',
            extension: 'kmz',
            mediaType: 'application/vnd.google-earth.kmz'
        },
        {
            id: 'geojson',
            label: 'GeoJSON',
            extension: ['json', 'geojson'],
            mediaType: [
                'application/json',
                'application/geo+json',
            ]
        },
        {
            id: 'gpx',
            label: 'GPX - GPS Exchange Format',
            extension: 'gpx',
            mediaType: 'application/gpx+xml'
        },
        {
            id: 'zippedShapefile',
            label: 'Zipped shapefile',
            extension: 'zip',
            mediaType: 'application/zip'
        },
        {
            id: 'shapefile',
            label: 'Shapefile',
            extension: 'shp',
            mediaType: MediaTypes.Shapefile
        },
    ];

    onSelectFile: (data: { file: File; extraFiles: File[]; fileType: any, dataProjection: string }) => void;

    public selectedFileType = this.fileTypes[0];

    public projections = [
        {
            id: "EPSG:4326",
            label: "WGS 84 - Longitude / Latitude"
        },
        {
            id: "EPSG:2193",
            label: "NZTM2000 - New Zealand Transverse Mercator 2000"
        },
        {
            id: "EPSG:3857",
            label: "Web Mercator"
        }
    ];

    fileType() {
        return this.file && this.file.type;
    }

    isShapefile() {
        return this.file
            && (this.file.type === MediaTypes.Shapefile || this.file.name.endsWith('.shp'));
    }

    fileIsLarge() {
        const FORTY_MB = 1024 * 1024 * 40;
        return this.file && this.file.size > FORTY_MB;
    }

    largeFileHelpLink() {
        const lt = window['LTConfiguration'] as any || {};
        const localDataConf = lt['localData'] || {};
        return localDataConf.largeDataHelp;
    }

    selectedProjection = this.projections[0];

    public next() {
        this.onSelectFile({
            file: this.file,
            extraFiles: this.extraFiles,
            fileType: this.selectedFileType,
            dataProjection: this.selectedProjection.id
        });
    }

    public isSelectedFileSupported() {
        return this.file &&
            (
                this.selectedFileType.id !== 'auto' || this.fileTypes.some(ft => fileMatches(this.file, ft.extension, ft.mediaType))
            );
    }

    $postLink(): void {
        const fileInput = this.$element[0].querySelector('input[type=file][name=file]') as HTMLInputElement;
        fileInput.addEventListener('change', (ev ) => {
            const target = ev.target;
            if (target instanceof HTMLInputElement) {
                const files = target.files;
                this.$scope.$apply(() => {
                    this.file = files ? files.item(0) : undefined;
                });
            }
        });

        this.$element[0].addEventListener('dragover', (ev) => {
            ev.stopPropagation();
            ev.preventDefault();
            if (ev.dataTransfer) {
                ev.dataTransfer.dropEffect = 'copy';
                this.$element[0].classList.add('drop-target');
                this.$element[0].querySelector('h1 > i.icon').classList.add('pulsate');
            }
        });

        this.$element[0].addEventListener('dragleave', () => {
            this.$element[0].classList.remove('drop-target');
            this.$element[0].querySelector('h1 > i.icon').classList.remove('pulsate');
        });

        this.$element[0].addEventListener('drop', (ev) => {
            ev.stopPropagation();
            ev.preventDefault();

            const transfer = ev.dataTransfer;
            if (transfer && transfer.files && transfer.files.length) {
                this.$scope.$apply(() => {
                    fileInput.files = transfer.files;
                    this.file = transfer.files.item(0)
                });
            }

            this.$element[0].classList.remove('drop-target');
            this.$element[0].querySelector('h1 > i.icon').classList.remove('pulsate');
        })

        const extraFilesInput = this.$element[0].querySelector('input[type=file][name=shapefile-extras]');
        extraFilesInput.addEventListener('change', (ev) => {
            const target = ev.target;

            const extraFileExtensions = [
                '.prj', '.dbf'
            ]

            if (target instanceof HTMLInputElement) {
                const files = target.files;
                this.$scope.$apply(() => {
                    this.extraFiles = [];
                    for (let i=0; i< files.length; i++) {
                        const file = files.item(i);
                        if (extraFileExtensions.some(ext => file.name.endsWith(ext))) {
                            this.extraFiles.push(file);
                        }
                    }
                });
            }
        })
    }
}

export class LocalDataFileSelector implements ng.IComponentOptions {
    controller = ['$element', '$scope', LocalDataFileSelectorController];
    bindings = {
        onSelectFile: '&',
        loading: '<'
    };
    templateUrl = '/Partial/component/LocalDataFileSelector'
}